import { createMemo, onMount, createSignal, batch } from 'solid-js'
import { Portal } from 'solid-js/web'
import { res2json } from '@/utils'
import { plays } from '@games'
import * as constants from '@lib/constants'
import { addToast } from '@/components/Toast'
import account from '@/store/account'

export default function BetRatio() {
  const [tab, setTab] = createSignal(account().fixed ? 'fixed' : 'fan')
  const [loadingFixed, setLoadingFixed] = createSignal(true)
  const [loadingFan, setLoadingFan] = createSignal(true)
  const loading = createMemo(() => (account().fixed && loadingFixed()) || (account().fan && loadingFan()))

  const hasFixed = createMemo(() => plays[account().game].indexOf('fixed') > -1)
  const hasFan = createMemo(() => plays[account().game].indexOf('fan') > -1)

  return (
    <>
      <div class="flex flex-col gap-4">
        <div class="breadcrumbs text-sm">
          <ul>
            <li><a href="/home">后台首页</a></li>
            <li>赔率限额设定</li>
          </ul>
        </div>
        <h2 class="text-xl">赔率限额设定</h2>
        <div role="tablist" class="tabs tabs-lifted">
          {hasFixed() && <>
            <input type="radio" name="bet-ratio" role="tab" onChange={e => { if (e.target.checked) setTab('fixed') }} checked={tab() === 'fixed'} class="tab whitespace-nowrap" aria-label="定位" />
            <div role="tabpanel" class="tab-content pt-2">
              {account().fixed ? <FixedBetRatio setLoading={setLoadingFixed} /> : <div class="pt-4">请付费开通</div>}
            </div>
          </>}
          {hasFan() && <>
            <input type="radio" name="bet-ratio" role="tab" onChange={e => { if (e.target.checked) setTab('fan') }} checked={tab() === 'fan'} class="tab whitespace-nowrap" aria-label="番摊" />
            <div role="tabpanel" class="tab-content pt-2">
              {account().fan ? <FanBetRatio setLoading={setLoadingFan} /> : <div class="pt-4">请付费开通</div>}
            </div>
          </>}
        </div>
      </div>

      <Portal mount={document.body}>
        {loading() && (<div class="fixed left-2 right-2 bottom-0"><progress class="progress progress-success w-full"></progress></div>)}
      </Portal>
    </>
  )
}

function FixedBetRatio({ setLoading }) {
  const [betRatioOne, setBetRatioOne] = createSignal(constants.defaultBetRatioOne)
  const [betRatioTwo, setBetRatioTwo] = createSignal(constants.defaultBetRatioTwo)
  const [betRatioThree, setBetRatioThree] = createSignal(constants.defaultBetRatioThree)
  const [betRatioFour, setBetRatioFour] = createSignal(constants.defaultBetRatioFour)
  const [betAmountOne, setBetAmountOne] = createSignal(constants.minBetAmountOne)
  const [betAmountTwo, setBetAmountTwo] = createSignal(constants.minBetAmountTwo)
  const [betAmountThree, setBetAmountThree] = createSignal(constants.minBetAmountThree)
  const [betAmountFour, setBetAmountFour] = createSignal(constants.minBetAmountFour)
  const [singleBetAmountOne, setSingleBetAmountOne] = createSignal(constants.maxBetAmountOne)
  const [singleBetAmountTwo, setSingleBetAmountTwo] = createSignal(constants.maxBetAmountTwo)
  const [singleBetAmountThree, setSingleBetAmountThree] = createSignal(constants.maxBetAmountThree)
  const [singleBetAmountFour, setSingleBetAmountFour] = createSignal(constants.maxBetAmountFour)
  const [singlePlayAmountOne, setSinglePlayAmountOne] = createSignal(constants.maxSinglePlayAmountOne)
  const [singlePlayAmountTwo, setSinglePlayAmountTwo] = createSignal(constants.maxSinglePlayAmountTwo)
  const [singlePlayAmountThree, setSinglePlayAmountThree] = createSignal(constants.maxSinglePlayAmountThree)
  const [singlePlayAmountFour, setSinglePlayAmountFour] = createSignal(constants.maxSinglePlayAmountFour)

  const [sending, setSending] = createSignal(false)
  const [errors, setErrors] = createSignal({})

  async function load() {
    setLoading(true)
    fetch(`/api/${account().game_code}/settings/bet-ratio`).then(res2json).then(r => {
      batch(() => {
        setBetRatioOne(r.data?.bet_ratio_one ?? constants.defaultBetRatioOne)
        setBetRatioTwo(r.data?.bet_ratio_two ?? constants.defaultBetRatioTwo)
        setBetRatioThree(r.data?.bet_ratio_three ?? constants.defaultBetRatioThree)
        setBetRatioFour(r.data?.bet_ratio_four ?? constants.defaultBetRatioFour)
        setBetAmountOne(r.data?.bet_amount_one ?? constants.minBetAmountOne)
        setBetAmountTwo(r.data?.bet_amount_two ?? constants.minBetAmountTwo)
        setBetAmountThree(r.data?.bet_amount_three ?? constants.minBetAmountThree)
        setBetAmountFour(r.data?.bet_amount_four ?? constants.minBetAmountFour)
        setSingleBetAmountOne(r.data?.single_bet_amount_one ?? constants.maxBetAmountOne)
        setSingleBetAmountTwo(r.data?.single_bet_amount_two ?? constants.maxBetAmountTwo)
        setSingleBetAmountThree(r.data?.single_bet_amount_three ?? constants.maxBetAmountThree)
        setSingleBetAmountFour(r.data?.single_bet_amount_four ?? constants.maxBetAmountFour)
        setSinglePlayAmountOne(r.data?.single_play_amount_one ?? constants.maxSinglePlayAmountOne)
        setSinglePlayAmountTwo(r.data?.single_play_amount_two ?? constants.maxSinglePlayAmountTwo)
        setSinglePlayAmountThree(r.data?.single_play_amount_three ?? constants.maxSinglePlayAmountThree)
        setSinglePlayAmountFour(r.data?.single_play_amount_four ?? constants.maxSinglePlayAmountFour)
      })
    }).finally(() => { setLoading(false) })
  }
  onMount(() => {
    load()
  })

  async function submit(e) {
    e.preventDefault()
    if (sending()) {
      return false
    }
    setSending(true)
    return fetch(`/api/${account().game_code}/settings/bet-ratio`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        bet_ratio_one: betRatioOne(),
        bet_ratio_two: betRatioTwo(),
        bet_ratio_three: betRatioThree(),
        bet_ratio_four: betRatioFour(),
        bet_amount_one: betAmountOne(),
        bet_amount_two: betAmountTwo(),
        bet_amount_three: betAmountThree(),
        bet_amount_four: betAmountFour(),
        single_bet_amount_one: singleBetAmountOne(),
        single_bet_amount_two: singleBetAmountTwo(),
        single_bet_amount_three: singleBetAmountThree(),
        single_bet_amount_four: singleBetAmountFour(),
        single_play_amount_one: singlePlayAmountOne(),
        single_play_amount_two: singlePlayAmountTwo(),
        single_play_amount_three: singlePlayAmountThree(),
        single_play_amount_four: singlePlayAmountFour(),
      })
    }).then(res2json).then(() => {
      addToast({ message: '已保存' })
    }).finally(() => { setSending(false) })
  }

  async function reset() {
    if (sending()) {
      return false
    }
    setSending(true)
    return fetch(`/api/${account().game_code}/settings/bet-ratio`, {
      method: 'DELETE'
    }).then(res2json).then(() => load()).then(() => {
      addToast({ message: '已重置' })
    }).finally(() => { setSending(false) })
  }

  return (
    <form onSubmit={submit} class="overflow-x-auto">
      <table class="table whitespace-nowrap">
        <thead>
          <tr class="bg-base-200">
            <th>玩法</th>
            <th>赔率</th>
            <th>最低投注</th>
            <th>单注最高</th>
            <th>单项最高</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>一字定位</td>
            <td>
              <input type="number" value={betRatioOne()} onChange={e => setBetRatioOne(e.target.value)} step="0.1" min={constants.minBetRatio} max={constants.maxBetRatioOne} class="input input-bordered input-sm w-24" />
              <span class="pl-2">最高: {constants.maxBetRatioOne}</span>
            </td>
            <td><input type="number" value={betAmountOne()} onChange={e => setBetAmountOne(e.target.value)} min={constants.minBetAmountOne} max={constants.maxBetAmountOne} class="input input-bordered input-sm w-24" /></td>
            <td><input type="number" value={singleBetAmountOne()} onChange={e => setSingleBetAmountOne(e.target.value)} min={constants.minBetAmountOne} max={constants.maxBetAmountOne} class="input input-bordered input-sm w-24" /></td>
            <td><input type="number" value={singlePlayAmountOne()} onChange={e => setSinglePlayAmountOne(e.target.value)} min={constants.minSinglePlayAmountOne} max={constants.maxSinglePlayAmountOne} class="input input-bordered input-sm w-24" /></td>
          </tr>
          <tr>
            <td>二字定位</td>
            <td>
              <input type="number" value={betRatioTwo()} onChange={e => setBetRatioTwo(e.target.value)} min={constants.minBetRatio} max={constants.maxBetRatioTwo} step="0.1" class="input input-bordered input-sm w-24" />
              <span class="pl-2">最高: {constants.maxBetRatioTwo}</span>
            </td>
            <td><input type="number" value={betAmountTwo()} onChange={e => setBetAmountTwo(e.target.value)} min={constants.minBetAmountTwo} max={constants.maxBetAmountTwo} class="input input-bordered input-sm w-24" /></td>
            <td><input type="number" value={singleBetAmountTwo()} onChange={e => setSingleBetAmountTwo(e.target.value)} min={constants.minBetAmountTwo} max={constants.maxBetAmountTwo} class="input input-bordered input-sm w-24" /></td>
            <td><input type="number" value={singlePlayAmountTwo()} onChange={e => setSinglePlayAmountTwo(e.target.value)} min={constants.minSinglePlayAmountTwo} max={constants.maxSinglePlayAmountTwo} class="input input-bordered input-sm w-24" /></td>
          </tr>
          <tr>
            <td>三字定位</td>
            <td>
              <input type="number" value={betRatioThree()} onChange={e => setBetRatioThree(e.target.value)} min={constants.minBetRatio} max={constants.maxBetRatioThree} step="0.1" class="input input-bordered input-sm w-24" />
              <span class="pl-2">最高: {constants.maxBetRatioThree}</span>
            </td>
            <td><input type="number" value={betAmountThree()} onChange={e => setBetAmountThree(e.target.value)} step="0.1" min={constants.minBetAmountThree} max={constants.maxBetAmountThree} class="input input-bordered input-sm w-24" /></td>
            <td><input type="number" value={singleBetAmountThree()} onChange={e => setSingleBetAmountThree(e.target.value)} step="0.1" min={constants.minBetAmountThree} max={constants.maxBetAmountThree} class="input input-bordered input-sm w-24" /></td>
            <td><input type="number" value={singlePlayAmountThree()} onChange={e => setSinglePlayAmountThree(e.target.value)} step="0.1" min={constants.minSinglePlayAmountThree} max={constants.maxSinglePlayAmountThree} class="input input-bordered input-sm w-24" /></td>
          </tr>
          <tr>
            <td>四字定位</td>
            <td>
              <input type="number" value={betRatioFour()} onChange={e => setBetRatioFour(e.target.value)} min={constants.minBetRatio} max={constants.maxBetRatioFour} step="0.1" class="input input-bordered input-sm w-24" />
              <span class="pl-2">最高: {constants.maxBetRatioFour}</span>
            </td>
            <td><input type="number" value={betAmountFour()} onChange={e => setBetAmountFour(e.target.value)} step="0.1" min={constants.minBetAmountFour} max={constants.maxBetAmountFour} class="input input-bordered input-sm w-24" /></td>
            <td><input type="number" value={singleBetAmountFour()} onChange={e => setSingleBetAmountFour(e.target.value)} step="0.1" min={constants.minBetAmountFour} max={constants.maxBetAmountFour} class="input input-bordered input-sm w-24" /></td>
            <td><input type="number" value={singlePlayAmountFour()} onChange={e => setSinglePlayAmountFour(e.target.value)} step="0.1" min={constants.minSinglePlayAmountFour} max={constants.maxSinglePlayAmountFour} class="input input-bordered input-sm w-24" /></td>
          </tr>
          <tr>
            <td colspan="5">
              <div class="grid grid-cols-2 md:grid-cols-5 gap-4 pt-4">
                <div class="hidden md:block md:col-span-3"></div>
                <button type="button" onClick={() => reset()} class="btn btn-full" disabled={sending()}>重置预设值</button>
                <button type="submit" class="btn btn-success text-white btn-full" disabled={sending()}>保存</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </form>
  )
}

function FanBetRatio({ setLoading }) {
  const [settings, setSettings] = createSignal(structuredClone(constants.defaultFanBetSettings))

  const [sending, setSending] = createSignal(false)
  const [errors, setErrors] = createSignal({})

  async function load() {
    setLoading(true)
    fetch(`/api/${account().game_code}/settings/fan-bet-ratio`).then(res2json).then(r => {
      setSettings(Object.keys(r.data).length ? r.data : structuredClone(constants.defaultFanBetSettings))
    }).finally(() => { setLoading(false) })
  }
  onMount(() => {
    load()
  })

  async function submit(e) {
    e.preventDefault()
    if (sending()) {
      return false
    }
    setSending(true)
    return fetch(`/api/${account().game_code}/settings/fan-bet-ratio`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(settings())
    }).then(res2json).then(() => {
      addToast({ message: '已保存' })
    }).finally(() => { setSending(false) })
  }

  async function reset() {
    if (sending()) {
      return false
    }
    setSending(true)
    return fetch(`/api/${account().game_code}/settings/fan-bet-ratio`, {
      method: 'DELETE'
    }).then(res2json).then(() => load()).then(() => {
      addToast({ message: '已重置' })
    }).finally(() => { setSending(false) })
  }

  const getValue = (key, at) => {
    return (settings()[key] ?? constants.defaultFanBetSettings[key])[at]
  }
  const setValue = (key, at, val) => setSettings(e => {
    e[key][at] = val
    return { ...e }
  })

  return (
    <form onSubmit={submit} class="overflow-x-auto">
      <table class="table whitespace-nowrap">
        <thead>
          <tr class="bg-base-200">
            <th>玩法</th>
            <th>赔率</th>
            <th>最低投注</th>
            <th>单注最高</th>
            <th>单项最高</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>番</td>
            <td>
              <input type="number" value={getValue('fan', 'bet_ratio')} onChange={e => setValue('fan', 'bet_ratio', e.target.value)} step="0.001" min={constants.minBetRatio} max={constants.maxFanBetRatio} class="input input-bordered input-sm w-24" />
              <span class="pl-2">最高: {constants.maxFanBetRatio}</span>
            </td>
            <td><input type="number" value={getValue('fan', 'min_bet_amount')} onChange={e => setValue('fan', 'min_bet_amount', e.target.value)} min={constants.minFanBetAmount} max={constants.maxFanBetAmount} class="input input-bordered input-sm w-24" /></td>
            <td><input type="number" value={getValue('fan', 'max_single_bet_amount')} onChange={e => setValue('fan', 'max_single_bet_amount', e.target.value)} min={constants.minFanBetAmount} max={constants.maxFanBetAmount} class="input input-bordered input-sm w-24" /></td>
            <td><input type="number" value={getValue('fan', 'max_signel_play_amount')} onChange={e => setValue('fan', 'max_signel_play_amount', e.target.value)} min={constants.minFanSinglePlayAmount} max={constants.maxFanSinglePlayAmount} class="input input-bordered input-sm w-24" /></td>
          </tr>
          <tr>
            <td>正</td>
            <td>
              <input type="number" value={getValue('zheng', 'bet_ratio')} onChange={e => setValue('zheng', 'bet_ratio', e.target.value)} step="0.001" min={constants.minBetRatio} max={constants.maxFanZhengBetRatio} class="input input-bordered input-sm w-24" />
              <span class="pl-2">最高: {constants.maxFanZhengBetRatio}</span>
            </td>
            <td><input type="number" value={getValue('zheng', 'min_bet_amount')} onChange={e => setValue('zheng', 'min_bet_amount', e.target.value)} min={constants.minFanBetAmount} max={constants.maxFanBetAmount} class="input input-bordered input-sm w-24" /></td>
            <td><input type="number" value={getValue('zheng', 'max_single_bet_amount')} onChange={e => setValue('zheng', 'max_single_bet_amount', e.target.value)} min={constants.minFanBetAmount} max={constants.maxFanBetAmount} class="input input-bordered input-sm w-24" /></td>
            <td><input type="number" value={getValue('zheng', 'max_signel_play_amount')} onChange={e => setValue('zheng', 'max_signel_play_amount', e.target.value)} min={constants.minFanSinglePlayAmount} max={constants.maxFanSinglePlayAmount} class="input input-bordered input-sm w-24" /></td>
          </tr>
          <tr>
            <td>念</td>
            <td>
              <input type="number" value={getValue('nian', 'bet_ratio')} onChange={e => setValue('nian', 'bet_ratio', e.target.value)} step="0.001" min={constants.minBetRatio} max={constants.maxFanNianBetRatio} class="input input-bordered input-sm w-24" />
              <span class="pl-2">最高: {constants.maxFanNianBetRatio}</span>
            </td>
            <td><input type="number" value={getValue('nian', 'min_bet_amount')} onChange={e => setValue('nian', 'min_bet_amount', e.target.value)} min={constants.minFanBetAmount} max={constants.maxFanBetAmount} class="input input-bordered input-sm w-24" /></td>
            <td><input type="number" value={getValue('nian', 'max_single_bet_amount')} onChange={e => setValue('nian', 'max_single_bet_amount', e.target.value)} min={constants.minFanBetAmount} max={constants.maxFanBetAmount} class="input input-bordered input-sm w-24" /></td>
            <td><input type="number" value={getValue('nian', 'max_signel_play_amount')} onChange={e => setValue('nian', 'max_signel_play_amount', e.target.value)} min={constants.minFanSinglePlayAmount} max={constants.maxFanSinglePlayAmount} class="input input-bordered input-sm w-24" /></td>
          </tr>
          <tr>
            <td>角</td>
            <td>
              <input type="number" value={getValue('jiao', 'bet_ratio')} onChange={e => setValue('jiao', 'bet_ratio', e.target.value)} step="0.001" min={constants.minBetRatio} max={constants.maxFanJiaoBetRatio} class="input input-bordered input-sm w-24" />
              <span class="pl-2">最高: {constants.maxFanJiaoBetRatio}</span>
            </td>
            <td><input type="number" value={getValue('jiao', 'min_bet_amount')} onChange={e => setValue('jiao', 'min_bet_amount', e.target.value)} min={constants.minFanBetAmount} max={constants.maxFanBetAmount} class="input input-bordered input-sm w-24" /></td>
            <td><input type="number" value={getValue('jiao', 'max_single_bet_amount')} onChange={e => setValue('jiao', 'max_single_bet_amount', e.target.value)} min={constants.minFanBetAmount} max={constants.maxFanBetAmount} class="input input-bordered input-sm w-24" /></td>
            <td><input type="number" value={getValue('jiao', 'max_signel_play_amount')} onChange={e => setValue('jiao', 'max_signel_play_amount', e.target.value)} min={constants.minFanSinglePlayAmount} max={constants.maxFanSinglePlayAmount} class="input input-bordered input-sm w-24" /></td>
          </tr>
          <tr>
            <td>单</td>
            <td>
              <input type="number" value={getValue('dan', 'bet_ratio')} onChange={e => setValue('dan', 'bet_ratio', e.target.value)} step="0.001" min={constants.minBetRatio} max={constants.maxFanDanBetRatio} class="input input-bordered input-sm w-24" />
              <span class="pl-2">最高: {constants.maxFanDanBetRatio}</span>
            </td>
            <td><input type="number" value={getValue('dan', 'min_bet_amount')} onChange={e => setValue('dan', 'min_bet_amount', e.target.value)} min={constants.minFanBetAmount} max={constants.maxFanBetAmount} class="input input-bordered input-sm w-24" /></td>
            <td><input type="number" value={getValue('dan', 'max_single_bet_amount')} onChange={e => setValue('dan', 'max_single_bet_amount', e.target.value)} min={constants.minFanBetAmount} max={constants.maxFanBetAmount} class="input input-bordered input-sm w-24" /></td>
            <td><input type="number" value={getValue('dan', 'max_signel_play_amount')} onChange={e => setValue('dan', 'max_signel_play_amount', e.target.value)} min={constants.minFanSinglePlayAmount} max={constants.maxFanSinglePlayAmount} class="input input-bordered input-sm w-24" /></td>
          </tr>
          <tr>
            <td>双</td>
            <td>
              <input type="number" value={getValue('shuang', 'bet_ratio')} onChange={e => setValue('shuang', 'bet_ratio', e.target.value)} step="0.001" min={constants.minBetRatio} max={constants.maxFanShuangBetRatio} class="input input-bordered input-sm w-24" />
              <span class="pl-2">最高: {constants.maxFanShuangBetRatio}</span>
            </td>
            <td><input type="number" value={getValue('shuang', 'min_bet_amount')} onChange={e => setValue('shuang', 'min_bet_amount', e.target.value)} min={constants.minFanBetAmount} max={constants.maxFanBetAmount} class="input input-bordered input-sm w-24" /></td>
            <td><input type="number" value={getValue('shuang', 'max_single_bet_amount')} onChange={e => setValue('shuang', 'max_single_bet_amount', e.target.value)} min={constants.minFanBetAmount} max={constants.maxFanBetAmount} class="input input-bordered input-sm w-24" /></td>
            <td><input type="number" value={getValue('shuang', 'max_signel_play_amount')} onChange={e => setValue('shuang', 'max_signel_play_amount', e.target.value)} min={constants.minFanSinglePlayAmount} max={constants.maxFanSinglePlayAmount} class="input input-bordered input-sm w-24" /></td>
          </tr>
          <tr>
            <td>大</td>
            <td>
              <input type="number" value={getValue('da', 'bet_ratio')} onChange={e => setValue('da', 'bet_ratio', e.target.value)} step="0.001" min={constants.minBetRatio} max={constants.maxFanDaBetRatio} class="input input-bordered input-sm w-24" />
              <span class="pl-2">最高: {constants.maxFanDaBetRatio}</span>
            </td>
            <td><input type="number" value={getValue('da', 'min_bet_amount')} onChange={e => setValue('da', 'min_bet_amount', e.target.value)} min={constants.minFanBetAmount} max={constants.maxFanBetAmount} class="input input-bordered input-sm w-24" /></td>
            <td><input type="number" value={getValue('da', 'max_single_bet_amount')} onChange={e => setValue('da', 'max_single_bet_amount', e.target.value)} min={constants.minFanBetAmount} max={constants.maxFanBetAmount} class="input input-bordered input-sm w-24" /></td>
            <td><input type="number" value={getValue('da', 'max_signel_play_amount')} onChange={e => setValue('da', 'max_signel_play_amount', e.target.value)} min={constants.minFanSinglePlayAmount} max={constants.maxFanSinglePlayAmount} class="input input-bordered input-sm w-24" /></td>
          </tr>
          <tr>
            <td>小</td>
            <td>
              <input type="number" value={getValue('xiao', 'bet_ratio')} onChange={e => setValue('xiao', 'bet_ratio', e.target.value)} step="0.001" min={constants.minBetRatio} max={constants.maxFanXiaoBetRatio} class="input input-bordered input-sm w-24" />
              <span class="pl-2">最高: {constants.maxFanXiaoBetRatio}</span>
            </td>
            <td><input type="number" value={getValue('xiao', 'min_bet_amount')} onChange={e => setValue('xiao', 'min_bet_amount', e.target.value)} min={constants.minFanBetAmount} max={constants.maxFanBetAmount} class="input input-bordered input-sm w-24" /></td>
            <td><input type="number" value={getValue('xiao', 'max_single_bet_amount')} onChange={e => setValue('xiao', 'max_single_bet_amount', e.target.value)} min={constants.minFanBetAmount} max={constants.maxFanBetAmount} class="input input-bordered input-sm w-24" /></td>
            <td><input type="number" value={getValue('xiao', 'max_signel_play_amount')} onChange={e => setValue('xiao', 'max_signel_play_amount', e.target.value)} min={constants.minFanSinglePlayAmount} max={constants.maxFanSinglePlayAmount} class="input input-bordered input-sm w-24" /></td>
          </tr>
          <tr>
            <td>通</td>
            <td>
              <input type="number" value={getValue('tong', 'bet_ratio')} onChange={e => setValue('tong', 'bet_ratio', e.target.value)} step="0.001" min={constants.minBetRatio} max={constants.maxFanTongBetRatio} class="input input-bordered input-sm w-24" />
              <span class="pl-2">最高: {constants.maxFanTongBetRatio}</span>
            </td>
            <td><input type="number" value={getValue('tong', 'min_bet_amount')} onChange={e => setValue('tong', 'min_bet_amount', e.target.value)} min={constants.minFanBetAmount} max={constants.maxFanBetAmount} class="input input-bordered input-sm w-24" /></td>
            <td><input type="number" value={getValue('tong', 'max_single_bet_amount')} onChange={e => setValue('tong', 'max_single_bet_amount', e.target.value)} min={constants.minFanBetAmount} max={constants.maxFanBetAmount} class="input input-bordered input-sm w-24" /></td>
            <td><input type="number" value={getValue('tong', 'max_signel_play_amount')} onChange={e => setValue('tong', 'max_signel_play_amount', e.target.value)} min={constants.minFanSinglePlayAmount} max={constants.maxFanSinglePlayAmount} class="input input-bordered input-sm w-24" /></td>
          </tr>
          <tr>
            <td>中</td>
            <td>
              <input type="number" value={getValue('zhong', 'bet_ratio')} onChange={e => setValue('zhong', 'bet_ratio', e.target.value)} step="0.001" min={constants.minBetRatio} max={constants.maxFanZhongBetRatio} class="input input-bordered input-sm w-24" />
              <span class="pl-2">最高: {constants.maxFanZhongBetRatio}</span>
            </td>
            <td><input type="number" value={getValue('zhong', 'min_bet_amount')} onChange={e => setValue('zhong', 'min_bet_amount', e.target.value)} min={constants.minFanBetAmount} max={constants.maxFanBetAmount} class="input input-bordered input-sm w-24" /></td>
            <td><input type="number" value={getValue('zhong', 'max_single_bet_amount')} onChange={e => setValue('zhong', 'max_single_bet_amount', e.target.value)} min={constants.minFanBetAmount} max={constants.maxFanBetAmount} class="input input-bordered input-sm w-24" /></td>
            <td><input type="number" value={getValue('zhong', 'max_signel_play_amount')} onChange={e => setValue('zhong', 'max_signel_play_amount', e.target.value)} min={constants.minFanSinglePlayAmount} max={constants.maxFanSinglePlayAmount} class="input input-bordered input-sm w-24" /></td>
          </tr>
          <tr>
            <td>加</td>
            <td>
              <input type="number" value={getValue('jia', 'bet_ratio')} onChange={e => setValue('jia', 'bet_ratio', e.target.value)} step="0.001" min={constants.minBetRatio} max={constants.maxFanJiaBetRatio} class="input input-bordered input-sm w-24" />
              <span class="pl-2">最高: {constants.maxFanJiaBetRatio}</span>
            </td>
            <td><input type="number" value={getValue('jia', 'min_bet_amount')} onChange={e => setValue('jia', 'min_bet_amount', e.target.value)} min={constants.minFanBetAmount} max={constants.maxFanBetAmount} class="input input-bordered input-sm w-24" /></td>
            <td><input type="number" value={getValue('jia', 'max_single_bet_amount')} onChange={e => setValue('jia', 'max_single_bet_amount', e.target.value)} min={constants.minFanBetAmount} max={constants.maxFanBetAmount} class="input input-bordered input-sm w-24" /></td>
            <td><input type="number" value={getValue('jia', 'max_signel_play_amount')} onChange={e => setValue('jia', 'max_signel_play_amount', e.target.value)} min={constants.minFanSinglePlayAmount} max={constants.maxFanSinglePlayAmount} class="input input-bordered input-sm w-24" /></td>
          </tr>
          <tr>
            <td colspan="5">
              <div class="grid grid-cols-2 md:grid-cols-5 gap-4 pt-4">
                <div class="hidden md:block md:col-span-3"></div>
                <button type="button" onClick={() => reset()} class="btn btn-full" disabled={sending()}>重置预设值</button>
                <button type="submit" class="btn btn-success text-white btn-full" disabled={sending()}>保存</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </form>
  )
}
